import { DeploymentType, useDeploymentHeartbeat } from "@/api";
import { useAppSessionSubscription } from "@/hooks/useAppSessionSubscription";
import AppFrame from "@/components/apps/AppFrame";
import { AppError, AppStopped } from "@/features/app-page";
import { AppDeploymentStatus } from "@/gql/__generated__/graphql";
import { ApolloError } from "@apollo/client";
import { Navigate } from "@tanstack/react-router";
import { AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { parseProxyURL } from "../utils/parseProxyURL";
import AppFilesButton from "./AppFiles/AppFilesButton";
import AppFilesDrawer from "./AppFiles/AppFilesDrawer";
import { NetworkErrorBanner } from "./NetworkErrorBanner";

interface Props {
  deploymentId: string;
  releaseNotesBase64: string | undefined;
  userGuideBase64: string | undefined;
  loading: boolean;
  error?: ApolloError;
  deploymentType: DeploymentType;
  isProxyAuthCookieSet?: boolean;
  appPath?: string;
  appSearchParams?: Record<string, unknown>;
}

const App = ({
  deploymentId,
  releaseNotesBase64,
  userGuideBase64,
  loading,
  error,
  deploymentType,
  isProxyAuthCookieSet = true,
  appPath,
  appSearchParams
}: Props) => {
  const {
    data,
    error: heartbeatError,
    networkError
  } = useDeploymentHeartbeat({
    deploymentId,
    deploymentType
  });

  const getSessionType = () => {
    switch (deploymentType) {
      case DeploymentType.Subscription:
        return "subscription";
      case DeploymentType.Shared:
        return "shared";
      default:
        return "organization";
    }
  };

  useAppSessionSubscription({
    type: getSessionType(),
    deployId: deploymentType === DeploymentType.Organization ? deploymentId : undefined,
    subscriptionId: deploymentType === DeploymentType.Subscription ? deploymentId : undefined,
    sharedUrlId: deploymentType === DeploymentType.Shared ? deploymentId : undefined
  });

  const [fetchProxyError, setFetchProxyError] = useState(false);
  const [proxyURL, setProxyURL] = useState("");
  const [status, setStatus] = useState(AppDeploymentStatus.Pending);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setProxyURL((current) => data?.proxyURL || current);
  }, [setProxyURL, data?.proxyURL]);

  useEffect(() => {
    setStatus((current) => data?.status || current);
  }, [setStatus, data?.status]);

  if (
    error ||
    fetchProxyError ||
    (heartbeatError && !networkError) ||
    status === AppDeploymentStatus.Error
  ) {
    if (
      error?.message === "access denied" ||
      heartbeatError?.message === "access denied"
    ) {
      console.error(
        "Access to this app is denied. Ask for access with organization administrator."
      );
      return <Navigate to="/access-denied" />;
    }

    return <AppError />;
  }

  if (status === AppDeploymentStatus.Stopped) return <AppStopped />;

  const isReady =
    status === AppDeploymentStatus.Running &&
    proxyURL !== "" &&
    !loading &&
    !error &&
    !(heartbeatError && !networkError) &&
    isProxyAuthCookieSet;

  const parsedProxyURL = parseProxyURL({ proxyURL, appPath, appSearchParams });

  return (
    <div className="flex flex-row justify-between">
      <AnimatePresence>
        {networkError && <NetworkErrorBanner />}
      </AnimatePresence>
      <AppFrame
        key={proxyURL}
        proxyUrl={parsedProxyURL}
        ready={isReady}
        onError={(iframeError) => {
          console.error(iframeError);
          setFetchProxyError(true);
        }}
      />
      {releaseNotesBase64 && userGuideBase64 && (
        <>
          <AppFilesButton isOpen={isOpen} setIsOpen={setIsOpen} />
          <AppFilesDrawer
            isOpen={isOpen}
            setIsOpen={() => setIsOpen((prev) => !prev)}
            releaseNotesBase64={releaseNotesBase64}
            userGuideBase64={userGuideBase64}
          />
        </>
      )}
    </div>
  );
};

export default App;
